import React from 'react';
import { Atividade, Dependencia } from '../../models/atividade';
import { buildChains, filtrarCaminhoCritico } from '../../utils/dependencyGraph';
import { useMaestroContext } from '../../context/MaestroContext';
import { formatarData } from '../../utils/formatters';
import { LABEL_AREA } from '../../utils/labels';
import Card from '../Card';
import EstadoVazio from '../EstadoVazio';
import StatusBadge from '../StatusBadge';

interface DependencyChainProps {
  atividades: Atividade[];
  dependencias: Dependencia[];
  modo: 'todas' | 'critico';
  onSelecionar: (id: number) => void;
}

const DependencyChain: React.FC<DependencyChainProps> = ({ atividades, dependencias, modo, onSelecionar }) => {
  const { getArea, getEmpresa, getUsuario } = useMaestroContext();
  const cadeias = React.useMemo(() => {
    const todas = buildChains(atividades, dependencias);
    return modo === 'critico' ? filtrarCaminhoCritico(todas) : todas;
  }, [atividades, dependencias, modo]);

  if (cadeias.length === 0) {
    return (
      <EstadoVazio
        icone='bi bi-diagram-3'
        titulo={modo === 'critico' ? 'Nenhum caminho crítico identificado neste escopo' : 'Nenhuma dependência entre atividades neste escopo'}
        descricao='Isso normalmente é bom sinal — nenhuma atividade está represando outra.'
      />
    );
  }

  const quantidadeCriticas = cadeias.filter(c => c.caminhoCritico).length;

  return (
    <div className='mc-dep-cadeias'>
      <p className='mc-dep-cadeias__resumo'>
        {modo === 'critico'
          ? <>Mostrando só as cadeias que fazem parte do caminho crítico do ciclo (<strong>{cadeias.length}</strong>).</>
          : <>Mostrando <strong>todas</strong> as cadeias de dependência do escopo (<strong>{cadeias.length}</strong>), sendo <strong>{quantidadeCriticas}</strong> no caminho crítico.</>}
      </p>
      {cadeias.map((cadeia, indice) => {
        const titulo = modo === 'critico' ? 'Caminho crítico' : `Cadeia de dependência ${indice + 1}`;
        return (
          <Card
            key={cadeia.id}
            className={cadeia.caminhoCritico ? 'mc-card--critico' : ''}
            title={titulo}
            actions={modo === 'todas' && cadeia.caminhoCritico ? <span className='mc-badge mc-badge--danger'><i className='bi bi-lightning-charge-fill' /> Caminho crítico</span> : undefined}
          >
            <div className='mc-dep-chain'>
              {cadeia.nos.map((no, index) => {
                const area = getArea(no.atividade.areaId);
                const empresa = getEmpresa(no.atividade.empresaId);
                const responsavel = getUsuario(no.atividade.responsavelId);
                return (
                  <div className='mc-dep-chain__no' key={no.atividade.id}>
                    {index > 0 && <div className='mc-dep-chain__conector' />}
                    <button type='button' className='mc-dep-chain__card' onClick={() => onSelecionar(no.atividade.id)}>
                      <div className='mc-dep-chain__topo'>
                        <strong>{no.atividade.nome}</strong>
                        <StatusBadge status={no.atividade.status} />
                      </div>
                      <small>{empresa?.nome} · {area ? LABEL_AREA[area.tipo] : ''} · {responsavel?.nome}</small>
                      {no.dependencia && (
                        <div className='mc-dep-chain__detalhe'>
                          <span><strong>Motivo:</strong> {no.dependencia.motivo}</span>
                          <span><strong>Prazo original:</strong> {formatarData(no.dependencia.prazoOriginal)} · <strong>Previsão atual:</strong> {formatarData(no.dependencia.previsaoAtual)}</span>
                          <span><strong>Atividades impactadas:</strong> {no.dependencia.quantidadeAtividadesImpactadas} · <strong>Impacto na previsão final:</strong> {no.dependencia.impactoNaPrevisaoFinalDias} dia(s) útil(eis)</span>
                          <span><strong>Ação recomendada:</strong> {no.dependencia.acaoRecomendada}</span>
                        </div>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default DependencyChain;
